import { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

export default function CandidatesListing() {

    // States
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);


    useEffect(() => {
        fetchCandidates();
    }, [currentPage, searchTerm]);

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const res = await makeRequestAuth(
                "post",
                `/admin/getCareerListing`,
                {
                    search: searchTerm,
                    pageNo: currentPage,
                    size: itemsPerPage,
                }
            );

            const data = res?.data?.list || [];
            setCandidates(data);

            // 🔥 total count backend se lena padega
            setTotalCount(res?.data?.total || 0);

        } catch (err) {
            console.error(err);
            showErrorToast("Failed to fetch candidates");
        } finally {
            setLoading(false);
        }
    };

    // Update Status API
    const handleStatusChange = async (id, newStatus) => {
        try {
            await makeRequestAuth(
                "post",
                `/admin/update-candidate-status`,
                { id, status: newStatus },
            );
            showSuccessToast(`Status updated to ${newStatus}`);

            // Local state update
            setCandidates((prev) =>
                prev.map((item) =>
                    item._id === id ? { ...item, status: newStatus } : item
                )
            );
        } catch (err) {
            console.error(err);
            showErrorToast("Status update failed!");
        }
    };

    // 🔥 2. Pagination Logic
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Status Badge Colors
    const getStatusStyle = (status) => {
        switch (status) {
            case "reviewed":
                return "bg-green-100 text-green-700";
            case "rejected":
                return "bg-red-100 text-red-700";
            default:
                return "bg-yellow-100 text-yellow-700"; // pending
        }
    };

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    return (
        <div className="min-h-screen bg-[#FFF7F3] pt-24 pb-12 px-4">
            <ToastContainer />
            <div className="max-w-7xl mx-auto">

                {/* HEADER & SEARCH */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                    <div>
                        <span className="text-[#FF6725] font-bold text-sm uppercase">Admin Panel</span>
                        <h1 className="text-3xl font-extrabold text-gray-900">Job Applications</h1>
                        <p className="text-sm text-gray-500 mt-1">Manage and review all your candidates here.</p>
                    </div>

                    {/* SEARCH BAR */}
                    <div className="relative w-full md:w-96">
                        <input
                            type="text"
                            placeholder="Search by name, email, position, status..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1); // Reset to page 1 on search
                            }}
                            className="w-full border p-3 pl-10 rounded-xl focus:ring-2 focus:ring-[#FF6725] focus:outline-none bg-white shadow-sm"
                        />
                        <span className="absolute left-3 top-3.5 text-gray-400">🔍</span>
                    </div>
                </div>

                {/* TABLE CONTAINER */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#FFF7F3] text-gray-700 uppercase text-xs font-bold">
                                <tr>
                                    <th className="px-6 py-4">Candidate Info</th>
                                    <th className="px-6 py-4">Applied For</th>
                                    <th className="px-6 py-4">Message</th>
                                    <th className="px-6 py-4">Resume</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-center">Actions</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100 text-sm">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-10 text-gray-500">
                                            <div className="w-6 h-6 border-4 border-[#FF6725] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                            Loading applications...
                                        </td>
                                    </tr>
                                ) : candidates.length > 0 ? (
                                    candidates.map((item) => (
                                        <tr key={item._id} className="hover:bg-gray-50 transition">

                                            {/* Name, Phone, Email */}
                                            <td className="px-6 py-4">
                                                <div className="font-semibold text-gray-900">{item.name}</div>
                                                <div className="text-gray-500 text-xs">{item.email}</div>
                                                <div className="text-gray-500 text-xs">{item.phone}</div>
                                            </td>

                                            {/* Position */}
                                            <td className="px-6 py-4 font-medium text-gray-700 capitalize">
                                                {item.position}
                                            </td>

                                            {/* Message */}
                                            <td className="px-6 py-4 max-w-xs truncate text-gray-600" title={item.message}>
                                                {item.message || "No message provided."}
                                            </td>

                                            {/* Resume Link */}
                                            <td className="px-6 py-4">
                                                {item.resume ? (
                                                    <a
                                                        href={item.resume}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[#FF6725] font-semibold hover:underline"
                                                    >
                                                        View CV 📄
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">Not Uploaded</span>
                                                )}
                                            </td>

                                            {/* Status Badge */}
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusStyle(item.status)}`}>
                                                    {item.status}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td className="px-6 py-4 text-center">
                                                <select
                                                    value={item.status}
                                                    onChange={(e) => handleStatusChange(item._id, e.target.value)}
                                                    className="border p-1.5 rounded-lg text-xs font-semibold focus:ring-2 focus:ring-[#FF6725] focus:outline-none"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="reviewed">Reviewed</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-10 text-gray-500">
                                            No candidates found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION CONTROLS */}
                    {totalPages > 1 && (
                        <div className="bg-[#FFF7F3] px-6 py-4 flex items-center justify-between border-t border-gray-100">
                            <p className="text-sm text-gray-600">
                                Showing <span className="font-semibold">{startItem}</span> to{" "}
                                <span className="font-semibold">{endItem}</span> of{" "}
                                <span className="font-semibold">{totalCount}</span> candidates
                            </p>

                            <div className="flex gap-2">
                                {/* Prev Button */}
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${currentPage === 1
                                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        : "bg-white border hover:bg-[#FF6725] hover:text-white"
                                        }`}
                                >
                                    Previous
                                </button>

                                {/* Page Numbers */}
                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index + 1}
                                        onClick={() => handlePageChange(index + 1)}
                                        className={`w-9 h-9 rounded-lg text-sm font-semibold transition ${currentPage === index + 1
                                            ? "bg-[#FF6725] text-white"
                                            : "bg-white border hover:bg-[#ffece2]"
                                            }`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}

                                {/* Next Button */}
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${currentPage === totalPages
                                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        : "bg-white border hover:bg-[#FF6725] hover:text-white"
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}